import { vendorUrls } from '@/api/urls/vendor';

import { formatStringByKey } from '@/utilities/formatStringByKey';
import { setApiOptions, setPaymentApiOptions } from '@/utilities/services';

import { api } from '../..';
import { PageableParam, PaginatedResponse } from '../models';
import { ReviewResponseType } from '../review/models';
import {
    addToCartBodyParamType,
    CartContent,
    CheckoutCartBodyParam,
    createPaymentForOrderBodyParam,
    CreateProductReviewBodyParam,
    PaymentCartBodyParam,
    PaymentOptions,
    PaymentOptionsBodyParam,
    PaymentQuote,
    SetCartCouponQueryParam,
    SubOrderChange,
    UpdateCartItemQueryParamsType,
    UpdateFullfilmentCartItemQueryParamsType,
    VendoreResponse
} from './models';

import {
    getOffersByProductUuidQueryParam,
    OfferContent,
    ProductOffersResponse,
    ProductsSearchQueryParamType,
    ServingCategoryType
} from '@/server/services/vendor/models';
import { AxiosPromise } from 'axios';
import { ListResponse, RequestResponseType } from 'lib/api/models';

export const getVendorsListService = (queryParam: any): AxiosPromise<unknown> => {
    const options = setApiOptions(vendorUrls.getVendorsList, { queryParam }, false);
    return api({ ...options, method: 'GET' });
};

export const getVendorBystoreUuidService = (storeUuid: string): AxiosPromise<RequestResponseType<VendoreResponse>> => {
    const options = setApiOptions(formatStringByKey(vendorUrls.getVendorBystoreUuid, { storeUuid }), false);
    return api({ ...options, method: 'GET' });
};

export const getServingCategoriesByStoreUuidService = (storeUuid: string): AxiosPromise<RequestResponseType<ServingCategoryType[]>> => {
    const options = setApiOptions(formatStringByKey(vendorUrls.getServingCategoriesByStoreUuid, { storeUuid }), false);
    return api({ ...options, method: 'GET' });
};

export const getFilteredProductsService = (queryParam: ProductsSearchQueryParamType): AxiosPromise<ListResponse<OfferContent>> => {
    const options = setApiOptions(vendorUrls.getFilteredProducts, { queryParam }, false);
    return api({ ...options, method: 'GET' });
};

export const getOffersByProductUuidService = (
    productUuid: string,
    queryParam: getOffersByProductUuidQueryParam
): AxiosPromise<RequestResponseType<ProductOffersResponse>> => {
    const options = setApiOptions(formatStringByKey(vendorUrls.getOffersByProductUuid, { productUuid }), { queryParam }, false);
    return api({ ...options, method: 'GET' });
};

export const addOfferToCartService = (bodyParam: addToCartBodyParamType): AxiosPromise<RequestResponseType<CartContent>> => {
    const options = setApiOptions(vendorUrls.addOfferToCart, { bodyParam }, false);
    return api({ ...options, method: 'POST' });
};

export const getCartService = (): AxiosPromise<RequestResponseType<CartContent[]>> => {
    const options = setApiOptions(vendorUrls.getOfferToCart, false);
    return api({ ...options, method: 'GET' });
};

export const updateCartService = (queryParam: UpdateCartItemQueryParamsType): AxiosPromise<RequestResponseType<CartContent>> => {
    const { cartId, id, ...body } = queryParam;
    const options = setApiOptions(formatStringByKey(vendorUrls.updateCart, { cartId, id }), { queryParam: body }, false);
    return api({ ...options, method: 'PATCH' });
};

export const deleteCartService = (cartId: string): AxiosPromise<RequestResponseType<CartContent[]>> => {
    const options = setApiOptions(formatStringByKey(vendorUrls.deleteCart, { cartId }), false);
    return api({ ...options, method: 'DELETE' });
};

export const deleteOfferFromCartService = (queryParam: UpdateCartItemQueryParamsType): AxiosPromise<RequestResponseType<[CartContent]>> => {
    const { cartId, id, ...body } = queryParam;
    const options = setApiOptions(formatStringByKey(vendorUrls.deleteOfferFromCart, { cartId, id }), { queryParam: body }, false);
    return api({ ...options, method: 'DELETE' });
};

export const updateFullfilmentCartItemService = (
    queryParam: UpdateFullfilmentCartItemQueryParamsType
): AxiosPromise<RequestResponseType<CartContent>> => {
    const { cartId, id } = queryParam;
    const hasInstallSlot = Boolean(queryParam.fulfillmentDate) && queryParam.fulfillmentAvailabilityRuleId != null;
    const bodyParam = hasInstallSlot
        ? {
              fulfillmentDate: queryParam.fulfillmentDate,
              fulfillmentAvailabilityRuleId: queryParam.fulfillmentAvailabilityRuleId,
              ...(queryParam.fulfillmentAddressId != null ? { fulfillmentAddressId: queryParam.fulfillmentAddressId } : {})
          }
        : {
              fulfillmentAddressId: queryParam.fulfillmentAddressId
          };
    const options = setApiOptions(
        formatStringByKey(vendorUrls.updateFullfilmentCartItem, { cartId, id }),
        {
            queryParam: { offerId: queryParam.offerId },
            bodyParam
        },
        false
    );
    return api({ ...options, method: 'PATCH' });
};

export const setCartCouponService = (cartId: string, queryParam: SetCartCouponQueryParam): AxiosPromise<RequestResponseType<CartContent>> => {
    const options = setApiOptions(formatStringByKey(vendorUrls.setCartCoupon, { cartId }), { queryParam });
    return api({ ...options, method: 'POST' });
};

export const removeCartCouponService = (cartId: string): AxiosPromise<RequestResponseType<CartContent>> => {
    const options = setApiOptions(formatStringByKey(vendorUrls.setCartCoupon, { cartId }));
    return api({ ...options, method: 'DELETE' });
};

export const checkoutCartService = (
    bodyParam: { cartId: string },
    dontHandleError = false
): AxiosPromise<RequestResponseType<CheckoutCartBodyParam>> => {
    const options = setApiOptions(vendorUrls.checkoutCart, { bodyParam });
    return api({ ...options, method: 'POST' }, dontHandleError);
};

export const createPaymentForOrderService = (
    orderId: string,
    bodyParam: createPaymentForOrderBodyParam
): AxiosPromise<RequestResponseType<PaymentCartBodyParam>> => {
    const options = setPaymentApiOptions(formatStringByKey(vendorUrls.createPaymentForOrder, { orderId }), { bodyParam });
    return api({ ...options, method: 'POST' });
};

export const getOrderPaymentOptionsService = (
    orderId: string,
    bodyParam: PaymentOptionsBodyParam
): AxiosPromise<RequestResponseType<PaymentOptions>> => {
    const options = setApiOptions(formatStringByKey(vendorUrls.getOrderPaymentOptions, { orderId }), { bodyParam });
    return api({ ...options, method: 'POST' });
};

export const getSubOrderChangePaymentOptionsService = (
    subOrderId: string,
    changeId: string,
    bodyParam: PaymentOptionsBodyParam
): AxiosPromise<RequestResponseType<PaymentOptions>> => {
    const options = setApiOptions(formatStringByKey(vendorUrls.getSubOrderChangePaymentOptions, { subOrderId, changeId }), { bodyParam });
    return api({ ...options, method: 'POST' });
};

export const quoteOrderPaymentService = (
    orderId: string,
    bodyParam: createPaymentForOrderBodyParam
): AxiosPromise<RequestResponseType<PaymentQuote>> => {
    const options = setApiOptions(formatStringByKey(vendorUrls.quoteOrderPayment, { orderId }), { bodyParam });
    return api({ ...options, method: 'POST' });
};

export const quoteSubOrderChangePaymentService = (
    subOrderId: string,
    changeId: string,
    bodyParam: createPaymentForOrderBodyParam
): AxiosPromise<RequestResponseType<PaymentQuote>> => {
    const options = setApiOptions(formatStringByKey(vendorUrls.quoteSubOrderChangePayment, { subOrderId, changeId }), { bodyParam });
    return api({ ...options, method: 'POST' });
};

export const listCustomerSubOrderChangesService = (
    subOrderId: string,
    dontHandleError = false
): AxiosPromise<RequestResponseType<SubOrderChange[]>> => {
    const options = setApiOptions(formatStringByKey(vendorUrls.listCustomerSubOrderChanges, { subOrderId }));
    return api({ ...options, method: 'GET' }, dontHandleError);
};

export const payCustomerSubOrderChangeService = (
    subOrderId: string,
    changeId: string,
    bodyParam: createPaymentForOrderBodyParam
): AxiosPromise<RequestResponseType<PaymentCartBodyParam>> => {
    const options = setPaymentApiOptions(formatStringByKey(vendorUrls.payCustomerSubOrderChange, { subOrderId, changeId }), { bodyParam });
    return api({ ...options, method: 'POST' });
};

export const createProductReviewService = (
    productUuid: string,
    bodyParam: CreateProductReviewBodyParam
): AxiosPromise<RequestResponseType<ReviewResponseType>> => {
    const options = setApiOptions(formatStringByKey(vendorUrls.createProductReview, { productUuid }), { bodyParam });
    return api({ ...options, method: 'POST' });
};

export const getReviewByStoreUUIDService = (storeUuid: string, bodyParam: PageableParam): AxiosPromise<PaginatedResponse<ReviewResponseType[]>> => {
    const options = setApiOptions(formatStringByKey(vendorUrls.getReviewByStoreUUID, { storeUuid }), { bodyParam });
    return api({ ...options, method: 'GET' });
};
